import * as React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogActions,
  DialogTitle,
  Button,
  Slide,
  Fade,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Chip,
  Popover,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Stack,
  Pagination,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
} from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AppAppBar from "../components/AppAppBar";
import Footer from "../components/Footer";
import EventCalendar from "../components/Calendar/EventCalendar";
import DashboardTable from "../components/Dashboard/DashboardTable";
import Title from "../components/Dashboard/Title";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import getLPTheme from "./getLPTheme";
import axios from "axios";
import CsvDownloadButton from "react-json-to-csv";

/**
 * MyCalendar - User's personal booking calendar page
 *
 * Displays the user's booked events in both calendar and table format,
 * with ability to view and delete bookings.
 *
 * @component
 * @example
 * <MyCalendar />
 */

const Transition = React.forwardRef(function Transition(props, ref) {
  return (
    <Slide
      direction='up'
      ref={ref}
      {...props}
    />
  );
});

// Helper function to get auth token from cookies
const getAuthToken = () => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("_auth="))
    ?.split("=")[1];
  return token;
};

/**
 * Check if a date is today
 * @param {string|Date} date - Date to check
 * @returns {boolean} True if the date is today
 */
const isToday = (date) => {
  const today = new Date();
  const checkDate = new Date(date);
  return (
    checkDate.getDate() === today.getDate() &&
    checkDate.getMonth() === today.getMonth() &&
    checkDate.getFullYear() === today.getFullYear()
  );
};

/**
 * Check if a date is within the current week
 * @param {string|Date} date - Date to check
 * @returns {boolean} True if the date is within the current week
 */
const isThisWeek = (date) => {
  const today = new Date();
  const checkDate = new Date(date);
  const startOfWeek = new Date(today);
  const endOfWeek = new Date(today);

  // Get start of week (Sunday)
  startOfWeek.setDate(today.getDate() - today.getDay());
  startOfWeek.setHours(0, 0, 0, 0);

  // Get end of week (Saturday)
  endOfWeek.setDate(today.getDate() + (6 - today.getDay()));
  endOfWeek.setHours(23, 59, 59, 999);

  return checkDate >= startOfWeek && checkDate <= endOfWeek;
};

/**
 * Determine meeting status based on current time
 * @param {string} startTime - Meeting start time
 * @param {string} endTime - Meeting end time
 * @returns {'upcoming'|'in-progress'|'completed'} Meeting status
 */
const getMeetingStatus = (startTime, endTime) => {
  const now = new Date();
  const start = new Date(startTime);
  const end = new Date(endTime);

  if (now < start) return "upcoming";
  if (now > end) return "completed";
  return "in-progress";
};

/**
 * Filter events for today - events are already sorted by start time (most recent first)
 * @param {Array} events - Array of events already sorted by start date DESC
 * @returns {Array} Today's events (maintaining API sort order)
 */
const getTodaysEvents = (events) => {
  return events.filter((event) => isToday(event.start));
};

/**
 * Filter events for this week - events are already sorted by start time (most recent first)
 * @param {Array} events - Array of events already sorted by start date DESC
 * @returns {Array} This week's events (maintaining API sort order)
 */
const getThisWeeksEvents = (events) => {
  return events.filter((event) => isThisWeek(event.start));
};

/**
 * Calculate total meeting time in minutes
 * @param {Array} events - Array of events
 * @returns {number} Total minutes
 */
const calculateTotalMeetingTime = (events) => {
  return events.reduce((total, event) => {
    const start = new Date(event.start);
    const end = new Date(event.end);
    const durationMinutes = (end - start) / (1000 * 60); // Convert milliseconds to minutes
    return total + durationMinutes;
  }, 0);
};

/**
 * Format time duration in a human-readable format
 * @param {number} minutes - Total minutes
 * @returns {string} Formatted time string (e.g., "2h 30m", "45m", "1h")
 */
const formatMeetingTime = (minutes) => {
  if (minutes === 0) return "0m";

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);

  if (hours === 0) {
    return `${remainingMinutes}m`;
  } else if (remainingMinutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${remainingMinutes}m`;
  }
};

/**
 * Get status chip color based on meeting status
 * @param {string} status - Meeting status
 * @returns {string} Color for the chip
 */
const getStatusColor = (status) => {
  switch (status) {
    case "upcoming":
      return "primary";
    case "in-progress":
      return "success";
    case "completed":
      return "default";
    default:
      return "default";
  }
};

/**
 * WeekOverview - Displays today's and this week's meetings overview
 *
 * @component
 * @param {Object} props - Component properties
 * @param {Array} props.events - Array of events
 * @param {boolean} props.loading - Loading state
 * @param {Object} props.theme - Material-UI theme object
 * @returns {JSX.Element} Week overview component
 */
const WeekOverview = ({ events, loading, theme }) => {
  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography
          variant='h5'
          sx={{ mb: 3 }}
        >
          Week Overview
        </Typography>
        <Grid
          container
          spacing={3}
        >
          <Grid
            item
            xs={12}
            md={4}
          >
            <Card>
              <CardContent>
                <Skeleton
                  variant='text'
                  width='60%'
                  height={32}
                />
                <Skeleton
                  variant='text'
                  width='40%'
                  height={24}
                  sx={{ mb: 2 }}
                />
                <Skeleton
                  variant='rectangular'
                  height={60}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Card>
              <CardContent>
                <Skeleton
                  variant='text'
                  width='60%'
                  height={32}
                />
                <Skeleton
                  variant='text'
                  width='40%'
                  height={24}
                  sx={{ mb: 2 }}
                />
                <Skeleton
                  variant='rectangular'
                  height={60}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Card>
              <CardContent>
                <Skeleton
                  variant='text'
                  width='60%'
                  height={32}
                />
                <Skeleton
                  variant='text'
                  width='40%'
                  height={24}
                  sx={{ mb: 2 }}
                />
                <Skeleton
                  variant='rectangular'
                  height={60}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    );
  }

  const todaysEvents = getTodaysEvents(events);
  const thisWeeksEvents = getThisWeeksEvents(events);
  const totalMeetingTimeThisWeek = calculateTotalMeetingTime(thisWeeksEvents);
  const totalMeetingTimeToday = calculateTotalMeetingTime(todaysEvents);

  /**
   * Render meeting card for individual meetings
   * @param {Object} event - Event object
   * @returns {JSX.Element} Meeting card
   */
  const MeetingCard = ({ event }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [attendees, setAttendees] = React.useState([]);
    const [attendeesLoading, setAttendeesLoading] = React.useState(false);
    const [attendeesFetched, setAttendeesFetched] = React.useState(false);
    const [actualAttendeeCount, setActualAttendeeCount] = React.useState(0);

    const status = getMeetingStatus(event.start, event.end);

    const timeStr = new Date(event.start).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    const endTimeStr = new Date(event.end).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    /**
     * Fetch attendee details from API and update actual count
     * @param {string} eventId - Event ID to fetch attendees for
     */
    const fetchAttendees = React.useCallback(
      async (eventId) => {
        if (attendeesFetched || attendeesLoading) return;

        setAttendeesLoading(true);
        try {
          const token = getAuthToken();
          if (!token) {
            console.error("No auth token found");
            setActualAttendeeCount(0);
            return;
          }

          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/get-event-attendees?eventId=${eventId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
              withCredentials: true,
            }
          );

          if (response.data && Array.isArray(response.data)) {
            setAttendees(response.data);
            setActualAttendeeCount(response.data.length);
          } else if (
            response.data &&
            response.data.attendees &&
            Array.isArray(response.data.attendees)
          ) {
            setAttendees(response.data.attendees);
            setActualAttendeeCount(response.data.attendees.length);
          } else {
            setAttendees([]);
            setActualAttendeeCount(0);
          }
          setAttendeesFetched(true);
        } catch (error) {
          console.error("Error fetching attendees:", error);
          setAttendees([]);
          setActualAttendeeCount(0);
          setAttendeesFetched(true);
        } finally {
          setAttendeesLoading(false);
        }
      },
      [attendeesFetched, attendeesLoading]
    );

    // Fetch attendees when component mounts
    React.useEffect(() => {
      if (event._id && !attendeesFetched) {
        fetchAttendees(event._id);
      }
    }, [event._id, attendeesFetched, fetchAttendees]);

    // Use actual attendee count if fetched, otherwise fall back to event data
    const displayAttendeeCount = attendeesFetched
      ? actualAttendeeCount
      : event.attendeeCount || event.attendees?.length || 0;

    /**
     * Handle attendee count hover
     * @param {Event} hoverEvent - Mouse event
     */
    const handleAttendeeHover = (hoverEvent) => {
      if (displayAttendeeCount > 0) {
        setAnchorEl(hoverEvent.currentTarget);
        // Fetch attendees if we haven't already
        if (!attendeesFetched && event._id) {
          fetchAttendees(event._id);
        }
      }
    };

    /**
     * Handle close attendee popover
     */
    const handleAttendeeClose = () => {
      setAnchorEl(null);
    };

    const openPopover = Boolean(anchorEl);

    return (
      <Box
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          mb: 1,
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: "primary.main",
            boxShadow: 1,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Typography
            variant='subtitle2'
            sx={{ fontWeight: 600 }}
          >
            {event.roomName}
          </Typography>
          <Chip
            label={status.replace("-", " ")}
            size='small'
            color={getStatusColor(status)}
            sx={{ textTransform: "capitalize" }}
          />
        </Box>
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ mb: 1 }}
        >
          {timeStr} - {endTimeStr}
        </Typography>
        <Typography
          variant='body2'
          sx={{ mb: 1 }}
        >
          {event.description}
        </Typography>

        {/* Attendee Count with Hover */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <PeopleIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{
              cursor: displayAttendeeCount > 0 ? "pointer" : "default",
              "&:hover":
                displayAttendeeCount > 0 ? { color: "primary.main" } : {},
              transition: "color 0.2s ease",
            }}
            onMouseEnter={handleAttendeeHover}
            onMouseLeave={handleAttendeeClose}
          >
            {attendeesLoading ? (
              <CircularProgress
                size={12}
                sx={{ ml: 0.5 }}
              />
            ) : (
              `${displayAttendeeCount} ${
                displayAttendeeCount === 1 ? "attendee" : "attendees"
              }`
            )}
          </Typography>
        </Box>

        {/* Attendee List Popover */}
        <Popover
          open={openPopover}
          anchorEl={anchorEl}
          onClose={handleAttendeeClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          sx={{
            pointerEvents: "none",
          }}
          slotProps={{
            paper: {
              sx: {
                pointerEvents: "auto",
                p: 1,
                maxWidth: 250,
                maxHeight: 200,
                overflow: "auto",
              },
            },
          }}
        >
          {attendeesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : attendees.length > 0 ? (
            <List
              dense
              sx={{ py: 0 }}
            >
              {attendees.map((attendee, index) => (
                <ListItem
                  key={index}
                  sx={{ px: 1, py: 0.5 }}
                >
                  <Stack
                    direction='row'
                    spacing={1}
                    alignItems='center'
                  >
                    <Avatar sx={{ width: 24, height: 24, fontSize: "0.75rem" }}>
                      {attendee.name
                        ? attendee.name.charAt(0).toUpperCase()
                        : attendee.email
                        ? attendee.email.charAt(0).toUpperCase()
                        : attendee.toString().charAt(0).toUpperCase()}
                    </Avatar>
                    <ListItemText
                      primary={
                        attendee.name ||
                        attendee.email ||
                        attendee.toString() ||
                        "Unknown User"
                      }
                      primaryTypographyProps={{
                        variant: "body2",
                        sx: { fontSize: "0.875rem" },
                      }}
                    />
                  </Stack>
                </ListItem>
              ))}
            </List>
          ) : attendeesFetched ? (
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ p: 1 }}
            >
              No attendees found
            </Typography>
          ) : (
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ p: 1 }}
            >
              Failed to load attendees
            </Typography>
          )}
        </Popover>
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Typography
        variant='h5'
        sx={{ mb: 3 }}
      >
        Week Overview
      </Typography>

      <Grid
        container
        spacing={3}
      >
        {/* Today's Meetings */}
        <Grid
          item
          xs={12}
          md={4}
        >
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography
                variant='h6'
                sx={{ mb: 1, color: "primary.main" }}
              >
                Today's Meetings
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mb: 2 }}
              >
                {todaysEvents.length} meeting
                {todaysEvents.length !== 1 ? "s" : ""}
              </Typography>
              <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
                {todaysEvents.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 2 }}>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                    >
                      No meetings today
                    </Typography>
                  </Box>
                ) : (
                  todaysEvents.map((event) => (
                    <MeetingCard
                      key={event._id}
                      event={event}
                    />
                  ))
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* This Week's Summary */}
        <Grid
          item
          xs={12}
          md={4}
        >
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography
                variant='h6'
                sx={{ mb: 1, color: "secondary.main" }}
              >
                This Week's Summary
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mb: 2 }}
              >
                {thisWeeksEvents.length} meeting
                {thisWeeksEvents.length !== 1 ? "s" : ""} total
              </Typography>
              <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
                {thisWeeksEvents.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 2 }}>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                    >
                      No meetings this week
                    </Typography>
                  </Box>
                ) : (
                  thisWeeksEvents
                    .filter((event) => !isToday(event.start)) // Exclude today's events
                    .slice(0, 3) // Show only next 3 meetings
                    .map((event) => (
                      <MeetingCard
                        key={event._id}
                        event={event}
                      />
                    ))
                )}
                {thisWeeksEvents.filter((event) => !isToday(event.start))
                  .length > 3 && (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ mt: 1, display: "block" }}
                  >
                    +
                    {thisWeeksEvents.filter((event) => !isToday(event.start))
                      .length - 3}{" "}
                    more meetings
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Stats */}
        <Grid
          item
          xs={12}
          md={4}
        >
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography
                variant='h6'
                sx={{ mb: 1 }}
              >
                Quick Stats
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant='body2'>Today:</Typography>
                  <Typography
                    variant='body2'
                    fontWeight={600}
                  >
                    {todaysEvents.length} meetings
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant='body2'>This Week:</Typography>
                  <Typography
                    variant='body2'
                    fontWeight={600}
                  >
                    {thisWeeksEvents.length} meetings
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant='body2'>Meeting Time Today:</Typography>
                  <Typography
                    variant='body2'
                    fontWeight={600}
                  >
                    {formatMeetingTime(totalMeetingTimeToday)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant='body2'>
                    Meeting Time This Week:
                  </Typography>
                  <Typography
                    variant='body2'
                    fontWeight={600}
                  >
                    {formatMeetingTime(totalMeetingTimeThisWeek)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant='body2'>Total Meeting Time:</Typography>
                  <Typography
                    variant='body2'
                    fontWeight={600}
                  >
                    {formatMeetingTime(calculateTotalMeetingTime(events))}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
};

/**
 * Format bookings for display in the table with proper date/time sorting
 *
 * @param {Array} bookingsData - Raw bookings data from API
 * @returns {Array} - Formatted bookings data with ISO dates for proper sorting
 */
const formatBookingsForDisplay = (bookingsData) => {
  if (!Array.isArray(bookingsData)) {
    console.error("bookingsData is not an array:", bookingsData);
    return [];
  }

  return bookingsData
    .map((booking) => {
      if (!booking) {
        console.error("Found null/undefined booking:", booking);
        return null;
      }

      // Validate that booking has start and end dates
      if (!booking.start || !booking.end) {
        console.error("Booking missing start or end date:", booking);
        return {
          ...booking,
          start: "Invalid Time",
          end: "Invalid Time",
        };
      }

      const startDate = new Date(booking.start);
      const endDate = new Date(booking.end);

      // Check if dates are valid
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.error("Invalid date format in booking:", booking);
        return {
          ...booking,
          start: "Invalid Time",
          end: "Invalid Time",
        };
      }

      return {
        ...booking,
        // Keep original ISO strings for proper date/time sorting
        date: booking.start, // ISO string for proper date sorting
        start: booking.start, // ISO string for proper time sorting
        end: booking.end, // ISO string for proper time sorting
      };
    })
    .filter(Boolean); // Remove any null entries
};

export default function MyCalendar() {
  const [mode, setMode] = useState("light");
  const LPtheme = createTheme(getLPTheme(mode));
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventActionLoading, setEventActionLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  // Table configuration to match RoomBookings.js format
  const columns = [
    "Date",
    "Start Time",
    "End Time",
    "Room",
    "Company Name",
    "Description",
    "Email",
  ];

  const columnMapping = {
    Date: "date",
    "Start Time": "start",
    "End Time": "end",
    Room: "roomName",
    "Company Name": "companyName",
    Description: "description",
    Email: "email",
  };

  // Format values for display - convert ISO strings to human-readable format
  const formatValues = {
    Date: (value) => {
      if (!value) return "N/A";
      try {
        return new Date(value).toLocaleDateString("en-US", {
          month: "numeric",
          day: "numeric",
          year: "numeric",
        });
      } catch (error) {
        return value;
      }
    },
    "Start Time": (value) => {
      if (!value) return "N/A";
      try {
        return new Date(value).toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      } catch (error) {
        return value;
      }
    },
    "End Time": (value) => {
      if (!value) return "N/A";
      try {
        return new Date(value).toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      } catch (error) {
        return value;
      }
    },
  };

  const toggleColorMode = () => {
    setMode((prev) => (prev === "dark" ? "light" : "dark"));
  };

  /**
   * Handle pagination page change
   * @param {Event} event - The pagination event
   * @param {number} newPage - New page number
   */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  /**
   * Handle pagination rows per page change
   * @param {Event} event - The select event
   */
  const handleChangeRowsPerPage = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  /**
   * Handle delete button click - opens confirmation dialog
   * @param {string} eventId - ID of the event to delete
   * @param {string} companyId - ID of the company associated with the event
   */
  const handleDeleteClick = (eventId, companyId) => {
    setSelectedEventId(eventId);
    setSelectedCompanyId(companyId);
    setDeleteDialogOpen(true);
  };

  /**
   * Confirm and execute event deletion
   */
  const handleConfirmDelete = async () => {
    setEventActionLoading(true);
    try {
      const token = getAuthToken();
      if (!token) {
        setError("Authentication token not found. Please log in again.");
        setShowAlert(true);
        return;
      }

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/delete-event`,
        {
          _id: selectedEventId,
          companyId: selectedCompanyId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      // Remove deleted event from local state
      setEvents(events.filter((event) => event._id !== selectedEventId));
      setSuccess("Event deleted successfully!");
      setShowAlert(true);
    } catch (error) {
      console.error("Error deleting event:", error);
      setError("Failed to delete event");
      setShowAlert(true);
    } finally {
      setEventActionLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  /**
   * Fetch user's events from API
   */
  useEffect(() => {
    const fetchEvents = async () => {
      setEventsLoading(true);
      setError(""); // Clear previous errors
      try {
        const token = getAuthToken();
        if (!token) {
          setError("Authentication token not found. Please log in again.");
          setShowAlert(true);
          setEventsLoading(false);
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/get-events-list?myEvents=true&limit=10000`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );

        // Process API response - API already returns data sorted by start date DESC
        if (response.data && Array.isArray(response.data.data)) {
          setEvents(response.data.data);
          // Calculate total pages
          setTotalPages(Math.ceil(response.data.data.length / limit) || 1);
        } else {
          console.warn(
            "API response for events is not in the expected format:",
            response.data
          );
          setEvents([]); // Set to empty array if format is wrong
          setTotalPages(1);
        }
      } catch (error) {
        console.error("Error fetching events:", error);
        // Handle specific errors like 401 Unauthorized
        if (error.response && error.response.status === 401) {
          setError("Your session may have expired. Please log in again.");
        } else {
          setError("Failed to load your bookings. Please try again later.");
        }
        setShowAlert(true);
        setEvents([]); // Clear events on error
      } finally {
        setEventsLoading(false);
      }
    };

    fetchEvents();
  }, [limit]); // Include limit dependency since it's used in calculating total pages

  // Recalculate total pages when limit changes
  useEffect(() => {
    // eslint-disable-line react-hooks/exhaustive-deps
    setTotalPages(Math.ceil(events.length / limit) || 1);
  }, [events.length, limit]);

  // Get all events - API already returns them sorted by date (most recent first)
  const getSortedEvents = () => {
    // API already returns events sorted by start date DESC, no need to sort again
    // Defensive programming: ensure we always return an array
    if (!Array.isArray(events)) {
      console.warn("events is not an array:", events);
      return [];
    }
    return events;
  };

  // Get paginated events for display
  const getPaginatedEvents = () => {
    const sortedEvents = getSortedEvents();
    const startIndex = (page - 1) * limit;
    const endIndex = Math.min(startIndex + limit, sortedEvents.length);
    return sortedEvents.slice(startIndex, endIndex);
  };

  return (
    <ThemeProvider theme={LPtheme}>
      <CssBaseline />
      <AppAppBar
        mode={mode}
        toggleColorMode={toggleColorMode}
      />
      <Box
        sx={{
          backgroundImage:
            "linear-gradient(180deg, rgba(203, 224, 15, 0.2), #fff)",
          backgroundSize: "100% 200px",
          backgroundRepeat: "no-repeat",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: { xs: 14, sm: 20 },
          px: 3,
        }}
      >
        <Container maxWidth='xl'>
          {/* Page Header */}
          <Typography
            variant='h3'
            component='h1'
            sx={{ mb: 4, textAlign: "center" }}
          >
            My Calendar
          </Typography>

          {/* Week Overview */}
          <WeekOverview
            events={events}
            loading={eventsLoading}
            theme={LPtheme}
          />

          {/* Calendar Section */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <EventCalendar
              roomName='My Bookings Calendar'
              roomid='my-bookings'
              eventsData={events}
              isLoading={eventsLoading}
              readOnly={true}
              defaultViewProp='month'
              subheaderText='View all your booked events in calendar format'
            />
          </Paper>

          {/* Bookings Table Section */}
          <Paper sx={{ p: 3, mb: 4 }}>
            {/* Error/Success Alerts */}
            {(error || success) && showAlert && (
              <Box sx={{ mb: 3 }}>
                {error && (
                  <Alert
                    variant='outlined'
                    severity='error'
                    onClose={() => setShowAlert(false)}
                  >
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert
                    variant='outlined'
                    severity='success'
                    onClose={() => setShowAlert(false)}
                  >
                    {success}
                  </Alert>
                )}
              </Box>
            )}

            <React.Fragment>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Title>My Bookings ({getSortedEvents().length} total)</Title>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <CsvDownloadButton
                    data={formatBookingsForDisplay(getSortedEvents())}
                    filename={"My Bookings.csv"}
                    style={{
                      background: "none",
                      border: "none",
                      padding: "0px",
                    }}
                  >
                    <FileDownloadOutlinedIcon />
                  </CsvDownloadButton>
                  <FormControl sx={{ width: 120 }}>
                    <InputLabel id='rows-per-page-label'>
                      Rows per page
                    </InputLabel>
                    <Select
                      labelId='rows-per-page-label'
                      id='rows-per-page'
                      value={limit}
                      label='Rows per page'
                      onChange={handleChangeRowsPerPage}
                      disabled={eventsLoading}
                      size='small'
                    >
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              {eventsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : events.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography color='text.secondary'>
                    No bookings found
                  </Typography>
                </Box>
              ) : (
                <>
                  <DashboardTable
                    data={formatBookingsForDisplay(getPaginatedEvents())}
                    columns={columns}
                    columnMapping={columnMapping}
                    formatValues={formatValues}
                    actionColumn={{
                      key: "actions",
                      label: "Actions",
                      render: (row) => {
                        // Check if meeting has started
                        const meetingStatus = getMeetingStatus(
                          row.start,
                          row.end
                        );
                        const hasStarted =
                          meetingStatus === "in-progress" ||
                          meetingStatus === "completed";
                        const tooltipTitle = hasStarted
                          ? "Cannot delete - Meeting has already started"
                          : "Delete Booking";

                        return (
                          <Fade in={true}>
                            <Tooltip title={tooltipTitle}>
                              <span>
                                {" "}
                                {/* Span wrapper needed for disabled button tooltip */}
                                <IconButton
                                  onClick={() =>
                                    handleDeleteClick(row._id, row.companyId)
                                  }
                                  disabled={eventActionLoading || hasStarted}
                                  color={hasStarted ? "default" : "error"}
                                  size='small'
                                  aria-label='delete event'
                                  sx={{
                                    "&:hover": hasStarted
                                      ? {}
                                      : {
                                          transform: "scale(1.1)",
                                        },
                                    opacity: hasStarted ? 0.5 : 1,
                                  }}
                                >
                                  <DeleteIcon fontSize='small' />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Fade>
                        );
                      },
                    }}
                  />
                  {totalPages > 0 && (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", mt: 2 }}
                    >
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handleChangePage}
                        color='primary'
                        disabled={eventsLoading}
                      />
                    </Box>
                  )}
                </>
              )}
            </React.Fragment>
          </Paper>
        </Container>
        <Footer />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setDeleteDialogOpen(false)}
        aria-describedby='alert-dialog-slide-description'
      >
        <DialogTitle>
          {"Are you sure you want to delete this booking?"}
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color='error'
            disabled={eventActionLoading}
          >
            {eventActionLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}
